import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import dayjs from "dayjs";
import {
  Box,
  IconButton,
  Popper,
  Paper,
  Checkbox,
  FormControlLabel,
  Typography,
  Button,
  TextField,
  ClickAwayListener,
  Divider,
  MenuItem,
  Collapse,
} from "@mui/material";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import FilterListOffRoundedIcon from "@mui/icons-material/FilterListOffRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpwardRounded";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownwardRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import { useTheme } from "@mui/material";

const ColumnDateFilter = ({
  data = [],
  field,
  onFilterChange,
  selectedValues = [],
  valueGetter,
  onSortChange,
  openFilter = null,
  setOpenFilter = null,
  currentSort = null,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchMonth, setSearchMonth] = useState("");
  const [searchDay, setSearchDay] = useState("");
  const [searchYear, setSearchYear] = useState("");
  const [localSelectedValues, setLocalSelectedValues] =
    useState(selectedValues);
  const [expandedMonths, setExpandedMonths] = useState({});
  const buttonRef = useRef(null);

  // Build hierarchical date structure
  const dateHierarchy = React.useMemo(() => {
    const hierarchy = {};

    data.forEach((row) => {
      const value = valueGetter ? valueGetter(null, row) : row[field];
      if (value === "N/A" || !value) {
        if (!hierarchy["(blanks)"]) {
          hierarchy["(blanks)"] = { type: "blank", value: "N/A" };
        }
        return;
      }

      const date = dayjs(value);
      if (!date.isValid()) return;

      const year = date.year();
      const month = date.format("MMMM");
      const day = date.date();

      if (!hierarchy[year]) {
        hierarchy[year] = { type: "year", months: {} };
      }

      if (!hierarchy[year].months[month]) {
        hierarchy[year].months[month] = { type: "month", days: {} };
      }

      if (!hierarchy[year].months[month].days[day]) {
        hierarchy[year].months[month].days[day] = { type: "day", values: [] };
      }

      hierarchy[year].months[month].days[day].values.push(value);
    });

    return hierarchy;
  }, [data, field, valueGetter]);

  // Get selection state for a node
  const getSelectionState = (values) => {
    if (!values || values.length === 0) return "none";
    const selectedCount = values.filter((v) =>
      localSelectedValues.includes(v),
    ).length;
    if (selectedCount === 0) return "none";
    if (selectedCount === values.length) return "all";
    return "partial";
  };

  // Get all values for a year
  const getYearValues = (yearData) => {
    const values = [];
    Object.values(yearData.months).forEach((monthData) => {
      Object.values(monthData.days).forEach((dayData) => {
        values.push(...dayData.values);
      });
    });
    return values;
  };

  // Get all values for a month
  const getMonthValues = (monthData) => {
    const values = [];
    Object.values(monthData.days).forEach((dayData) => {
      values.push(...dayData.values);
    });
    return values;
  };

  useEffect(() => {
    if (selectedValues.length === 0) {
      // If no filter is active, show all items as selected
      const allValues = [];
      Object.entries(dateHierarchy).forEach(([key, value]) => {
        if (key === "(blanks)") {
          allValues.push("N/A");
        } else {
          allValues.push(...getYearValues(value));
        }
      });
      setLocalSelectedValues([...new Set(allValues)]);
    } else {
      setLocalSelectedValues(selectedValues);
    }
  }, [selectedValues, dateHierarchy]);

  // Use global state if provided, otherwise use local state
  const isOpen =
    openFilter !== undefined ? openFilter === field : Boolean(anchorEl);
  const anchorElement = isOpen ? buttonRef.current : null;
  const open = isOpen;

  const handleClick = () => {
    if (setOpenFilter) {
      // Use global state - always close any open filter first, then open this one if it wasn't already open
      if (openFilter === field) {
        setOpenFilter(null);
      } else {
        setOpenFilter(field);
      }
    } else {
      // Use local state (backward compatibility)
      setAnchorEl(anchorEl ? null : buttonRef.current);
    }
  };

  const handleClose = useCallback(() => {
    if (setOpenFilter) {
      // Only close if this filter is currently open
      if (openFilter === field) {
        setOpenFilter(null);
      }
    } else {
      setAnchorEl(null);
    }
    setSearchMonth("");
    setSearchDay("");
    setSearchYear("");
  }, [field, openFilter, setOpenFilter]);

  // ESC key listener
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && open) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, handleClose]);

  const handleSelectAll = () => {
    const allValues = [];
    Object.entries(dateHierarchy).forEach(([key, value]) => {
      if (key === "(blanks)") {
        allValues.push("N/A");
      } else {
        allValues.push(...getYearValues(value));
      }
    });
    setLocalSelectedValues([...new Set(allValues)]);
  };

  const handleSelectNone = () => {
    setLocalSelectedValues([]);
  };

  const handleYearToggle = (year) => {
    if (year === "(blanks)") {
      const isSelected = localSelectedValues.includes("N/A");
      if (isSelected) {
        setLocalSelectedValues((prev) => prev.filter((v) => v !== "N/A"));
      } else {
        setLocalSelectedValues((prev) => [...prev, "N/A"]);
      }
      return;
    }

    const yearValues = getYearValues(dateHierarchy[year]);
    const state = getSelectionState(yearValues);

    if (state === "all") {
      // Deselect all
      setLocalSelectedValues((prev) =>
        prev.filter((v) => !yearValues.includes(v)),
      );
    } else {
      // Select all
      setLocalSelectedValues((prev) => [...new Set([...prev, ...yearValues])]);
    }
  };

  const handleMonthToggle = (year, month) => {
    const monthValues = getMonthValues(dateHierarchy[year].months[month]);
    const state = getSelectionState(monthValues);

    if (state === "all") {
      // Deselect all
      setLocalSelectedValues((prev) =>
        prev.filter((v) => !monthValues.includes(v)),
      );
    } else {
      // Select all
      setLocalSelectedValues((prev) => [...new Set([...prev, ...monthValues])]);
    }
  };

  const handleDayToggle = (year, month, day) => {
    const dayValues = dateHierarchy[year].months[month].days[day].values;
    const state = getSelectionState(dayValues);

    if (state === "all") {
      // Deselect all
      setLocalSelectedValues((prev) =>
        prev.filter((v) => !dayValues.includes(v)),
      );
    } else {
      // Select all
      setLocalSelectedValues((prev) => [...new Set([...prev, ...dayValues])]);
    }
  };

  const handleMonthExpand = (year, month) => {
    const key = `${year}-${month}`;
    setExpandedMonths((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleApply = () => {
    let finalSelectedValues = localSelectedValues;

    // If there's any search term, only keep values that match the search
    if (searchMonth || searchDay || searchYear) {
      const searchedValues = [];
      Object.entries(filteredHierarchy).forEach(([key, value]) => {
        if (key === "(blanks)") {
          searchedValues.push("N/A");
        } else {
          searchedValues.push(...getYearValues(value));
        }
      });
      finalSelectedValues = localSelectedValues.filter((v) =>
        searchedValues.includes(v),
      );
    }

    onFilterChange(finalSelectedValues);
    handleClose();
  };

  const handleCancel = () => {
    setLocalSelectedValues(selectedValues);
    handleClose();
  };

  const allValues = React.useMemo(() => {
    const values = [];
    Object.entries(dateHierarchy).forEach(([key, value]) => {
      if (key === "(blanks)") {
        values.push("N/A");
      } else {
        values.push(...getYearValues(value));
      }
    });
    return [...new Set(values)];
  }, [dateHierarchy]);
  const hasActiveFilter =
    selectedValues.length > 0 && selectedValues.length < allValues.length;

  // Filter hierarchy based on search
  const filteredHierarchy = React.useMemo(() => {
    if (!searchMonth && !searchDay && !searchYear) return dateHierarchy;

    const filtered = {};

    Object.entries(dateHierarchy).forEach(([year, yearData]) => {
      if (year === "(blanks)") {
        if (
          "(blanks)".toLowerCase().includes(searchMonth.toLowerCase()) ||
          "(blanks)".toLowerCase().includes(searchDay.toLowerCase()) ||
          "(blanks)".toLowerCase().includes(searchYear.toLowerCase())
        ) {
          filtered[year] = yearData;
        }
        return;
      }

      // Check if year matches
      if (searchYear && !String(year).includes(searchYear)) {
        return;
      }

      const filteredYear = { type: "year", months: {} };
      let hasMatch = false;

      Object.entries(yearData.months).forEach(([month, monthData]) => {
        const monthOrder = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];
        const monthNumber = String(monthOrder.indexOf(month) + 1).padStart(
          2,
          "0",
        );
        const monthNumberNoZero = String(parseInt(monthNumber, 10));
        const monthName = month.toLowerCase();

        // Check if month matches
        if (searchMonth) {
          const searchLower = searchMonth.toLowerCase();
          const monthMatches =
            monthNumber === searchMonth ||
            monthNumberNoZero === searchMonth ||
            monthName.includes(searchLower);
          if (!monthMatches) {
            return;
          }
        }

        // Filter days
        const filteredMonth = { type: "month", days: {} };
        let monthHasMatch = false;

        Object.entries(monthData.days).forEach(([day, dayData]) => {
          // Check if day matches
          if (searchDay && !String(day).includes(searchDay)) {
            return;
          }

          filteredMonth.days[day] = dayData;
          monthHasMatch = true;
          hasMatch = true;
        });

        if (monthHasMatch) {
          filteredYear.months[month] = filteredMonth;
        }
      });

      if (hasMatch) {
        filtered[year] = filteredYear;
      }
    });

    return filtered;
  }, [dateHierarchy, searchMonth, searchDay, searchYear]);

  // Auto-expand all months when filter opens or when searching
  useEffect(() => {
    if (open || searchMonth || searchDay || searchYear) {
      const allMonthKeys = {};
      Object.entries(filteredHierarchy).forEach(([year, yearData]) => {
        if (year !== "(blanks)" && yearData.months) {
          Object.keys(yearData.months).forEach((month) => {
            allMonthKeys[`${year}-${month}`] = true;
          });
        }
      });
      setExpandedMonths(allMonthKeys);
    }
  }, [open, filteredHierarchy, searchMonth, searchDay, searchYear]);

  const handleClearFilter = () => {
    onFilterChange([]);
    handleClose();
  };

  return (
    <>
      <IconButton
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          handleClick();
        }}
        size="small"
        sx={{
          color: hasActiveFilter
            ? theme.palette.primary.main
            : theme.palette.primary.text2,
          opacity: hasActiveFilter ? 1 : 0,
          transition: "opacity 0.2s",
          ".MuiDataGrid-columnHeader:hover &": {
            opacity: 1,
          },
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        {hasActiveFilter ? (
          <FilterListOffRoundedIcon fontSize="small" />
        ) : (
          <FilterListRoundedIcon fontSize="small" />
        )}
      </IconButton>

      {/* Backdrop */}
      {open &&
        createPortal(
          <Box
            onClick={handleClose}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "transparent",
              cursor: "default",
              zIndex: 9998,
            }}
          />,
          document.body,
        )}

      <Popper
        open={open}
        anchorEl={anchorElement}
        placement="bottom-start"
        style={{ zIndex: 9999 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={8}
            sx={{
              width: 320,
              maxHeight: 500,
              py: 1.25,
              px: 2,
              bgcolor: theme.palette.background.default,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "12px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Clear Filter Button */}
            {hasActiveFilter && (
              <>
                <Button
                  onClick={handleClearFilter}
                  size="small"
                  variant="text"
                  color="primary"
                  startIcon={<ClearIcon />}
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  Clear Filter
                </Button>
                <Divider sx={{ mb: 1 }} />
              </>
            )}

            {/* Search Fields */}
            <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
              <TextField
                size="small"
                placeholder="Month"
                value={searchMonth}
                onChange={(e) => setSearchMonth(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    Object.keys(filteredHierarchy).length > 0 &&
                    localSelectedValues.length > 0
                  ) {
                    handleApply();
                  }
                }}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                placeholder="Day"
                value={searchDay}
                onChange={(e) => setSearchDay(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    Object.keys(filteredHierarchy).length > 0 &&
                    localSelectedValues.length > 0
                  ) {
                    handleApply();
                  }
                }}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                placeholder="Year"
                value={searchYear}
                onChange={(e) => setSearchYear(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    Object.keys(filteredHierarchy).length > 0 &&
                    localSelectedValues.length > 0
                  ) {
                    handleApply();
                  }
                }}
                sx={{ flex: 1 }}
              />
            </Box>

            {/* Sort Options */}
            {onSortChange && (
              <>
                <Box sx={{ display: "flex", mt: 1 }}>
                  <MenuItem
                    sx={{
                      flex: 1,
                      justifyContent: "center",
                      borderRadius: "6px",
                      backgroundColor:
                        currentSort === "asc"
                          ? theme.palette.background.default
                          : "transparent",
                    }}
                    onClick={() => {
                      if (currentSort === "asc") {
                        onSortChange(field, null);
                      } else {
                        onSortChange(field, "asc");
                      }
                      handleClose();
                    }}
                  >
                    <ArrowUpwardIcon
                      sx={{
                        mr: 1,
                        fontSize: "small",
                        color:
                          currentSort === "asc"
                            ? theme.palette.primary.main
                            : theme.palette.primary.text2,
                      }}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={currentSort === "asc" ? "bold" : "normal"}
                      color={
                        currentSort === "asc"
                          ? theme.palette.primary.main
                          : theme.palette.primary.text2
                      }
                    >
                      Sort A-Z
                    </Typography>
                  </MenuItem>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                  <MenuItem
                    sx={{
                      flex: 1,
                      justifyContent: "center",
                      backgroundColor:
                        currentSort === "desc"
                          ? theme.palette.background.default
                          : "transparent",
                      borderRadius: "6px",
                    }}
                    onClick={() => {
                      if (currentSort === "desc") {
                        onSortChange(field, null);
                      } else {
                        onSortChange(field, "desc");
                      }
                      handleClose();
                    }}
                  >
                    <ArrowDownwardIcon
                      sx={{
                        mr: 1,
                        fontSize: "small",
                        color:
                          currentSort === "desc"
                            ? theme.palette.primary.main
                            : theme.palette.primary.text2,
                      }}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={currentSort === "desc" ? "bold" : "normal"}
                      color={
                        currentSort === "desc"
                          ? theme.palette.primary.main
                          : theme.palette.primary.text2
                      }
                    >
                      Sort Z-A
                    </Typography>
                  </MenuItem>
                </Box>
                <Divider sx={{ mt: 1 }} />
              </>
            )}

            {/* Hierarchical Date List */}
            <Box
              sx={{
                maxHeight: 300,
                overflowY: "auto",
                overflowX: "hidden",
                flex: 1,
                minHeight: 0,
                mt: 0.5,
              }}
            >
              {/* Select All Item */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={localSelectedValues.length === allValues.length}
                    indeterminate={
                      localSelectedValues.length > 0 &&
                      localSelectedValues.length < allValues.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleSelectAll();
                      } else {
                        handleSelectNone();
                      }
                    }}
                    size="small"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                  >
                    Select All
                  </Typography>
                }
                sx={{
                  display: "flex",
                  width: "100%",
                  m: 0,
                  mb: 0.5,
                  "& .MuiFormControlLabel-label": {
                    flex: 1,
                  },
                }}
              />
              <Divider sx={{ mb: 0.5 }} />
              {Object.entries(filteredHierarchy)
                .sort(([a], [b]) => {
                  if (a === "(blanks)") return 1;
                  if (b === "(blanks)") return -1;
                  return b.localeCompare(a); // Newest years first
                })
                .map(([year, yearData]) => {
                  if (year === "(blanks)") {
                    return (
                      <FormControlLabel
                        key="blanks"
                        control={
                          <Checkbox
                            checked={localSelectedValues.includes("N/A")}
                            onChange={() => handleYearToggle("(blanks)")}
                            size="small"
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.875rem" }}
                          >
                            (blanks)
                          </Typography>
                        }
                        sx={{
                          display: "flex",
                          width: "100%",
                          m: 0,
                          "& .MuiFormControlLabel-label": {
                            flex: 1,
                          },
                        }}
                      />
                    );
                  }

                  const yearValues = getYearValues(yearData);
                  const yearState = getSelectionState(yearValues);

                  return (
                    <Box key={year}>
                      {/* Year Level */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={yearState === "all"}
                              indeterminate={yearState === "partial"}
                              onChange={() => handleYearToggle(year)}
                              size="small"
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                            >
                              {year}
                            </Typography>
                          }
                          sx={{
                            display: "flex",
                            width: "100%",
                            m: 0,
                            "& .MuiFormControlLabel-label": {
                              flex: 1,
                            },
                          }}
                        />
                      </Box>

                      {/* Months Level - Always expanded */}
                      <Box sx={{ ml: 2 }}>
                        {Object.entries(yearData.months)
                          .sort(([a], [b]) => {
                            const monthOrder = [
                              "January",
                              "February",
                              "March",
                              "April",
                              "May",
                              "June",
                              "July",
                              "August",
                              "September",
                              "October",
                              "November",
                              "December",
                            ];
                            return (
                              monthOrder.indexOf(a) - monthOrder.indexOf(b)
                            );
                          })
                          .map(([month, monthData]) => {
                            const monthValues = getMonthValues(monthData);
                            const monthState = getSelectionState(monthValues);

                            return (
                              <Box key={`${year}-${month}`}>
                                {/* Month Level */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleMonthExpand(year, month)
                                    }
                                    sx={{ p: 0.5 }}
                                  >
                                    {expandedMonths[`${year}-${month}`] ? (
                                      <ExpandMoreIcon fontSize="small" />
                                    ) : (
                                      <ChevronRightIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={monthState === "all"}
                                        indeterminate={monthState === "partial"}
                                        onChange={() =>
                                          handleMonthToggle(year, month)
                                        }
                                        size="small"
                                      />
                                    }
                                    label={
                                      <Typography
                                        variant="body2"
                                        sx={{ fontSize: "0.875rem" }}
                                      >
                                        {month}
                                      </Typography>
                                    }
                                    sx={{
                                      display: "flex",
                                      width: "100%",
                                      m: 0,
                                      "& .MuiFormControlLabel-label": {
                                        flex: 1,
                                      },
                                    }}
                                  />
                                </Box>

                                {/* Days Level */}
                                <Collapse
                                  in={expandedMonths[`${year}-${month}`]}
                                >
                                  <Box sx={{ ml: 2 }}>
                                    {Object.entries(monthData.days)
                                      .sort(
                                        (
                                          [dayA, dayDataA],
                                          [dayB, dayDataB],
                                        ) => {
                                          // Compare actual dates, not just day numbers
                                          const dateA = dayjs(
                                            dayDataA.values[0],
                                          );
                                          const dateB = dayjs(
                                            dayDataB.values[0],
                                          );
                                          return dateA.isBefore(dateB)
                                            ? -1
                                            : dateA.isAfter(dateB)
                                              ? 1
                                              : 0;
                                        },
                                      )
                                      .map(([day, dayData]) => {
                                        const dayValues = dayData.values;
                                        const dayState =
                                          getSelectionState(dayValues);
                                        return (
                                          <FormControlLabel
                                            key={`${year}-${month}-${day}`}
                                            control={
                                              <Checkbox
                                                checked={dayState === "all"}
                                                indeterminate={
                                                  dayState === "partial"
                                                }
                                                onChange={() =>
                                                  handleDayToggle(
                                                    year,
                                                    month,
                                                    day,
                                                  )
                                                }
                                                size="small"
                                              />
                                            }
                                            label={
                                              <Typography
                                                variant="body2"
                                                sx={{ fontSize: "0.875rem" }}
                                              >
                                                {day}
                                              </Typography>
                                            }
                                            sx={{
                                              display: "flex",
                                              width: "100%",
                                              m: 0,
                                              ml: 3,
                                              "& .MuiFormControlLabel-label": {
                                                flex: 1,
                                              },
                                            }}
                                          />
                                        );
                                      })}
                                  </Box>
                                </Collapse>
                              </Box>
                            );
                          })}
                      </Box>
                    </Box>
                  );
                })}
            </Box>

            <Divider sx={{ my: 0.5 }} />

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                mt: 0.75,
                flexShrink: 0,
              }}
            >
              <Button onClick={handleCancel} size="small" variant="outlined">
                Cancel
              </Button>
              <Button
                onClick={handleApply}
                size="small"
                variant="contained"
                disabled={localSelectedValues.length === 0}
              >
                Apply
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default ColumnDateFilter;
